import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, createSearchParams } from 'react-router-dom'
import { CreateReportProps, Report } from "../../backend/types/report.ts"
import { Document, Unpopulated } from "../../backend/types/document.ts"
import { mutationHandler } from "../mutationHandler.ts"
import { TableParams } from "../../types/tableParams.ts"
import { PaginatedResponse } from "../../types/paginated.ts"
import {getErrorCode, handleResponse} from "../handleResponse.ts"
import { AddressBalanceMap } from "../../backend/types/moneyflow.ts"
import { downloadCSV } from "../../utils/download.ts"

const createReport = (meta: Omit<CreateReportProps, 'origin'>) => fetch(`/api/report`, {
    method: 'POST',
    headers: {
        "Content-Type": "application/json",
    },
    body: JSON.stringify(meta)
})

export const useCreateReportMutation = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: mutationHandler<Document<Unpopulated>, typeof createReport>(createReport),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`task`] })
        },
    })
}

const getReportWithFilters = (reportId?: string, params?: TableParams) => fetch(`/api/report/${reportId}`, {
    method: 'POST',
    headers: {
        "Content-Type": "application/json",
    },
    body: JSON.stringify({ reportId, ...params })
})

export const useGetReport = (
    reportId?: string, params?: TableParams
) => {
    const navigate = useNavigate()

    return useQuery<PaginatedResponse<Document<Report>>>({
        queryKey: [`report/${reportId}`, params],
        queryFn: async () => handleResponse(await getReportWithFilters(reportId, params)),
        enabled: !!reportId,
        retry: (failureCount, error) => {
            const errorCode = getErrorCode(error)

            if (errorCode === '400' || errorCode === '403') {
                navigate({
                    pathname: "/",
                    search: createSearchParams({
                        errorId: 'bad-report-id'
                    }).toString()
                })
            }

            return failureCount < 3
        }
    })
}

export const useGetReportEntityBalance = (reportId: string, entityId: string) => useQuery<AddressBalanceMap>({
    queryKey: [`report/${reportId}/entityBalance/${entityId}`],
    enabled: !!reportId
})

type ReportGenerationParams = {
    includeLabels: boolean
    useUserLabels: boolean
}

const generateCSVReport = ({ reportId, params } : {reportId: string, params?:ReportGenerationParams}) =>
    fetch(`/api/report/${reportId}/csv`, {
        method: 'POST',
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(params)
    })

export const useGenerateCSVReportMutation = () => useMutation({
    mutationFn: mutationHandler<{ csv: string, filename: string }, typeof generateCSVReport>(generateCSVReport),
    onSuccess: ({ csv, filename }) => {
        downloadCSV(csv, filename)
    },
})

